// Seznam položek Katalogu položek pro našeptávač bez psaní (27. 10. 2026, zadavatel: „ať když klikne na pole
// vyhledávání, rovnou vidí všechny varianty položek“ — u odpadu, materiálu, zboží, služby i zpětného odběru).
// Jeden společný zdroj pro všechna pole, kde se vybírá položka katalogu (zakázka, Můj den, vážení, nabídka, ceníky,
// sklad): `seznamBezTextuPolozek(…)` vrátí vše, co `Naseptavac` potřebuje.
//
//   - Krátké typy (materiál, zboží, služba, zpětný odběr, obal) se načtou jednou celé (až 500 položek) a seřadí podle
//     kódu, u typů s podtypem (zpětný odběr: elektrozařízení / baterie, materiál) podle podtypu; nadpisy skupin dělí
//     seznam na oddíly.
//   - Odpad (stovky kódů) se načítá po stránkách po 100 podle kódu, nadpisy jsou skupiny katalogu (dvojčíslí kódu:
//     „15 — Odpadní obaly…“); další stránka se dočte posunem nebo tlačítkem „Zobrazit další“.
//   - Více typů v jednom poli (rychlé přidání do zakázky): typ po typu, malé typy napřed, odpad nakonec.
//   - Nahoře oddíly podle zákazníka zakázky (`kontextZakaznika`): „Ve smlouvě zákazníka“ — položky, které má zákazník
//     (a provozovna) ve smluvních cenách, tentýž zdroj jako karta zákazníka, ale jen kód, název a jednotka, ceny
//     server řidiči a skladníkovi nevrací (`GET /polozky?ve_smlouve_zakaznika_id=`) — a pod ním „Časté u tohoto
//     zákazníka / provozovny“ (`nejcastejsi=zakaznik`). Bez zákazníka v kontextu se oddíly nevykreslí.
//   - Cache: react-query drží seznam 30 minut (stejný klíč = sdílený mezi poli), takže se na tabletu netahá při každém
//     klepnutí. Navíc se každá stránka (bez cen a bez variant zákazníka) uloží do úložiště tabletu (IndexedDB) a při
//     výpadku signálu se vezme odtud. Balíček dat na směnu (`offline/balicek.ts`) tyto seznamy i položky ze smlouvy
//     zákazníků jízd dne stahuje dopředu (`predstahniKatalogProTeren`), takže Můj den je ukáže i bez signálu.
//   - Stav skladu (`sklad`, 31. 10. 2026): pole, kde se vybírá kód k prodeji (podklad od odběratele, položka zakázky
//     s nakládkou u nás), pošle IČZ a server dá ke skladovým položkám `skladem` (jen tomu, kdo smí číst sklad).
//     Seznam se stavem platí jen 2 minuty a do tabletu se neukládá (stav se mění během dne); bez signálu se vezme
//     uložený seznam bez stavu.
//   - Kódy skladem nahoře (`sklademNahore`, 1. 11. 2026): u dokladu od odběratele je nad číselníkem oddíl „Skladem na
//     CZL00673“ — jen kódy se stavem > 0 (z 881 kódů má skoro všechny 0 kg); kód z něj se pod ním neopakuje. Při psaní
//     hledaného textu stojí nahoře kódy skladem ze všech nalezených (ne jen z prvních 20), pod nadpisem „Ostatní
//     kódy“ zbytek. Oddíl dává server (`jen_skladem`) jen tomu, kdo stav vidí; ostatní mají seznam beze změny.
//     `sklad_kontext=podklad_prodeje` dá číslo na CZL00673 i účetní (`prodej.podklad`) bez `sklad.cist` (05/4830 b).
import { api, ChybaApi } from '../api/klient';
import {
  KLIC_KATALOG,
  type FiltrPolozek,
  type KontextStavu,
  NAZEV_PODTYP_NADPIS,
  NAZEV_TYPU,
  nacistPolozky,
  podtypyProTyp,
  TYPY_POLOZEK,
  type Polozka,
  type PolozkaSeznam,
  type TypPolozky,
} from '../api/katalog';
import { jeOffline } from '../offline/sit';
import { uloziste } from '../offline/uloziste';
import { nadpisSkupinyOdpadu } from '../odpady';
import type { VysledekHledani } from './Naseptavac';
import type {
  CasteVSeznamu,
  OddilNahore,
  SeznamBezTextu,
  StrankaSeznamu,
} from './naseptavac-seznam';

/** Kolik odpadů se načte najednou — katalog odpadů jde po stránkách, ať první klepnutí nečeká na stovky řádků. */
export const STRANKA_ODPADU = 100;
/** Ostatní typy se načtou celé (server dovolí 500). */
export const STRANKA_OSTATNICH = 500;
/** Kolik častých položek se nabídne nahoře. */
export const POCET_CASTYCH = 8;

export interface VolbySeznamuPolozek {
  /** Jen položky těchto typů; bez nich všechny typy. */
  typy?: readonly TypPolozky[];
  /** Jen odpady povolené na zařízení příjemce (vážení). */
  zarizeniId?: string;
  /** Varianty tohoto zákazníka ponesou položky (ceny zákazníka, nabídka) — seznam se pak neukládá do tabletu. */
  zakaznikId?: string;
  /** Zákazník (a provozovna) zakázky: oddíly „Ve smlouvě zákazníka“ a „Časté u tohoto zákazníka / provozovny“ nahoře. */
  kontextZakaznika?: { zakaznikId: string; provozovnaId?: string };
  /** Jen položky, které smí dodat občan (veřejný řádek Ceníku sběrný dvůr) — nabídka pro občana (27. 10. 2026). */
  proObcana?: boolean;
  /** Jen položky platného Ceníku sběrný dvůr (hledání skladníka u příjmu na skladě — W84, 30. 10. 2026). */
  cenikSd?: boolean;
  /** IČZ našeho zařízení: položky (i v oddílech nahoře) ponesou stav skladu `skladem` (31. 10. 2026). */
  sklad?: string;
  /** Doklad od odběratele a faktura z něj: stav na CZL00673 dostane i účetní (`prodej.podklad`) — jen se `sklad`. */
  skladKontext?: KontextStavu;
  /** Kódy se stavem skladu > 0 nahoře v oddílu „Skladem na …“ (doklad od odběratele, 1. 11. 2026); jen se `sklad`. */
  sklademNahore?: boolean;
}

/** Jak dlouho (min) platí seznam se stavem skladu — stav se mění s každým příjmem a výdejem. */
export const PLATNOST_SE_STAVEM_SKLADU_MIN = 2;

/** Pořadí typů při více typech v jednom poli: malé napřed, odpad (stovky) nakonec. */
function poradiTypu(typy: readonly TypPolozky[] | undefined): TypPolozky[] {
  const vsechny = typy?.length ? [...new Set(typy)] : [...TYPY_POLOZEK];
  return [...vsechny.filter((t) => t !== 'odpad'), ...vsechny.filter((t) => t === 'odpad')];
}

/** Nadpis skupiny položky: odpad skupinou katalogu, typy s podtypem podtypem; u více typů s názvem typu. */
export function nadpisSkupinyPolozky(p: Polozka, viceTypu: boolean): string | null {
  let skupina: string | null = null;
  if (p.typ === 'odpad') skupina = nadpisSkupinyOdpadu(p.kod);
  else if (p.podtyp) skupina = NAZEV_PODTYP_NADPIS[p.podtyp];
  if (!viceTypu) return skupina;
  return skupina ? `${NAZEV_TYPU[p.typ]} · ${skupina}` : NAZEV_TYPU[p.typ];
}

/** Typy s podtypem se v seznamu řadí podle podtypu (stabilně, uvnitř podtypu podle kódu ze serveru). */
function seraditPodlePodtypu(typ: TypPolozky, polozky: Polozka[]): Polozka[] {
  const poradi = podtypyProTyp(typ);
  if (poradi.length === 0) return polozky;
  const index = (p: Polozka) => {
    const i = p.podtyp ? poradi.indexOf(p.podtyp) : -1;
    return i < 0 ? poradi.length : i;
  };
  return polozky
    .map((p, i) => ({ p, i }))
    .sort((a, b) => index(a.p) - index(b.p) || a.i - b.i)
    .map((x) => x.p);
}

/**
 * Do tabletu bez cen a bez stavu skladu: výchozí cena položky patří jen těm, kdo ceny vidí, a úložiště tabletu ji nemá
 * držet; stav skladu by v kopii zastaral.
 */
function bezCen(stranka: StrankaSeznamu<Polozka>): StrankaSeznamu<Polozka> {
  return {
    ...stranka,
    polozky: stranka.polozky.map((p) => {
      const kopie = { ...p };
      delete kopie.jednotkova_cena_vychozi;
      delete kopie.skladem;
      return kopie;
    }),
  };
}

const jePoruchaSite = (chyba: unknown) =>
  jeOffline() || (chyba instanceof ChybaApi && chyba.status === 0);

/** Parametr stránky = pořadí typu × 1000 + stránka v typu (typy se načítají jeden po druhém). */
const PO_TYPU = 1000;

async function nacistStranku(
  param: number,
  typy: readonly TypPolozky[],
  volby: VolbySeznamuPolozek,
  signal: AbortSignal,
): Promise<StrankaSeznamu<Polozka>> {
  const viceTypu = typy.length > 1;
  let index = Math.floor(param / PO_TYPU);
  let strana = param % PO_TYPU || 1;
  // Prázdné typy se přeskočí hned, ať nabídka nezůstane bez řádků s tlačítkem „Zobrazit další“.
  for (;;) {
    const typ = typy[index];
    if (!typ) return { polozky: [], dalsiStrana: null };
    const velikost = typ === 'odpad' ? STRANKA_ODPADU : STRANKA_OSTATNICH;
    const q = new URLSearchParams({
      typ,
      aktivni: 'true',
      razeni: 'kod:asc',
      velikost: String(velikost),
      strana: String(strana),
    });
    if (volby.zarizeniId && typ === 'odpad') q.set('zarizeni_id', volby.zarizeniId);
    if (volby.zakaznikId) q.set('zakaznik_id', volby.zakaznikId);
    if (volby.proObcana) q.set('pro_obcana', 'true');
    if (volby.cenikSd) q.set('cenik_sd', 'true');
    if (volby.sklad) {
      q.set('sklad', volby.sklad);
      if (volby.skladKontext) q.set('sklad_kontext', volby.skladKontext);
    }
    const r = await api.get<PolozkaSeznam>(`/polozky?${q.toString()}`, { signal });
    const dalsiVTypu = strana * velikost < r.celkem;
    const dalsiStrana = dalsiVTypu
      ? index * PO_TYPU + strana + 1
      : index + 1 < typy.length
        ? (index + 1) * PO_TYPU + 1
        : null;
    if (r.polozky.length === 0 && dalsiStrana !== null) {
      index = Math.floor(dalsiStrana / PO_TYPU);
      strana = dalsiStrana % PO_TYPU || 1;
      continue;
    }
    return {
      polozky: seraditPodlePodtypu(typ, r.polozky),
      ...(viceTypu ? {} : { celkem: r.celkem }),
      dalsiStrana,
    };
  }
}

function klicUlozeni(typy: readonly TypPolozky[], volby: VolbySeznamuPolozek, param: number) {
  return `katalog-seznam:${typy.join(',')}:${volby.zarizeniId ?? ''}:${volby.proObcana ? 'obcan:' : ''}${volby.cenikSd ? 'cenik-sd:' : ''}${param}`;
}

/**
 * Stránka ze serveru; při výpadku signálu poslední uložená v tabletu (jen seznamy bez variant zákazníka). Seznam se
 * stavem skladu se neukládá (stav by zastaral), bez signálu ale vezme uloženou kopii téhož seznamu bez stavu.
 */
async function stranaSOfflinem(
  param: number,
  typy: readonly TypPolozky[],
  volby: VolbySeznamuPolozek,
  signal: AbortSignal,
): Promise<StrankaSeznamu<Polozka>> {
  const klic = klicUlozeni(typy, volby, param);
  try {
    const stranka = await nacistStranku(param, typy, volby, signal);
    if (!volby.zakaznikId && !volby.sklad) await ulozitDoTabletu(klic, bezCen(stranka));
    return stranka;
  } catch (chyba) {
    if (!volby.zakaznikId && jePoruchaSite(chyba)) {
      const ulozena = await ulozenoVTabletu<StrankaSeznamu<Polozka>>(klic);
      if (ulozena) return ulozena.hodnota;
    }
    throw chyba;
  }
}

/** Kopie v tabletu s časem pořízení (pro „je ještě čerstvá?“ při předstahování). */
interface UlozenaKopie<T> {
  cas: number;
  hodnota: T;
}

async function ulozitDoTabletu(klic: string, hodnota: unknown): Promise<void> {
  try {
    await uloziste().uloz(klic, { cas: Date.now(), hodnota } satisfies UlozenaKopie<unknown>);
  } catch {
    // Úložiště plné nebo zakázané — seznam se prostě příště načte znovu.
  }
}

async function ulozenoVTabletu<T>(klic: string): Promise<UlozenaKopie<T> | undefined> {
  try {
    return await uloziste().ziskej<UlozenaKopie<T>>(klic);
  } catch {
    return undefined;
  }
}

/** Kolik kódů skladem se nabídne nahoře (kódů se stavem > 0 je zlomek katalogu; zbytek zůstane v seznamu pod nimi). */
export const POCET_SKLADEM_NAHORE = 200;

/** Nadpis oddílu kódů se stavem skladu > 0. */
export const nadpisSkladem = (icz: string) => `Skladem na ${icz}`;

/** Nadpis zbytku výsledků hledání pod oddílem „Skladem“. */
export const NADPIS_OSTATNI_KODY = 'Ostatní kódy';

/**
 * Kódy, které máme skladem (stav > 0) na zařízení `volby.sklad`, ve stejných omezeních jako seznam (zařízení příjemce,
 * zákazník, občan, Ceník sběrný dvůr), po typech v pořadí `typy` a v nich podle kódu. Server (`jen_skladem`) je dá jen
 * tomu, kdo stav vidí — jinak nic. Se `hledat` jen ty, které hledanému textu odpovídají.
 */
export async function nacistKodySkladem(
  typy: readonly TypPolozky[],
  volby: VolbySeznamuPolozek,
  signal: AbortSignal,
  hledat?: string,
): Promise<Polozka[]> {
  const sklad = volby.sklad;
  if (!sklad) return [];
  const casti = await Promise.all(
    typy.map((typ) => {
      const filtr: FiltrPolozek = {
        typ,
        sklad,
        jenSkladem: true,
        velikost: POCET_SKLADEM_NAHORE,
        ...(volby.skladKontext ? { skladKontext: volby.skladKontext } : {}),
        ...(volby.zarizeniId && typ === 'odpad' ? { zarizeniId: volby.zarizeniId } : {}),
        ...(volby.zakaznikId ? { zakaznikId: volby.zakaznikId } : {}),
        ...(volby.proObcana ? { proObcana: true } : {}),
        ...(volby.cenikSd ? { cenikSd: true } : {}),
        ...(hledat ? { hledat } : {}),
      };
      return nacistPolozky(filtr, signal);
    }),
  );
  // Server stav jen filtruje; číslo u položky je to, co člověk uvidí — bez něj do oddílu nepatří nic.
  return casti.flatMap((r) => r.polozky).filter((p) => (p.skladem?.mnozstvi ?? 0) > 0);
}

const klicNabidky = (p: Polozka) => p.id ?? `${p.typ}:${p.kod}`;
const jeSkladem = (p: Polozka) => (p.skladem?.mnozstvi ?? 0) > 0;

/**
 * Hledání v našeptávači se kódy skladem nahoře: dotaz jako dosud (nejvýš `velikost` nalezených) a vedle něj stejné
 * hledání jen mezi kódy skladem — ať nahoře jsou všechny, ne jen ty z prvních 20. Kód skladem se pod nimi neopakuje.
 * Oddíl je doplněk: když jeho dotaz selže, nahoru jdou aspoň kódy skladem z běžných výsledků a hledání funguje dál.
 */
export async function hledatSeSklademNahore(
  filtr: FiltrPolozek & { sklad: string },
  signal: AbortSignal,
): Promise<VysledekHledani<Polozka>> {
  const [vsechny, skladem] = await Promise.all([
    nacistPolozky(filtr, signal),
    nacistKodySkladem(
      [filtr.typ],
      { sklad: filtr.sklad, ...(filtr.skladKontext ? { skladKontext: filtr.skladKontext } : {}) },
      signal,
      filtr.hledat,
    ).catch((): Polozka[] => []),
  ]);
  const nahore = new Map(skladem.map((p) => [klicNabidky(p), p] as const));
  for (const p of vsechny.polozky)
    if (jeSkladem(p) && !nahore.has(klicNabidky(p))) nahore.set(klicNabidky(p), p);
  return {
    polozky: [...nahore.values(), ...vsechny.polozky.filter((p) => !nahore.has(klicNabidky(p)))],
    celkem: vsechny.celkem,
  };
}

/**
 * Nadpisy k výsledkům hledání s kódy skladem nahoře: „Skladem na CZL00673“ před prvním kódem skladem a „Ostatní kódy“
 * před prvním dalším. Bez kódu skladem (nebo bez práva stav vidět) žádný nadpis — seznam je jako dosud.
 */
export function nadpisyKoduSkladem(polozky: readonly Polozka[], icz: string): (string | null)[] {
  const kolik = polozky.findIndex((p) => !jeSkladem(p));
  const skladem = kolik < 0 ? polozky.length : kolik;
  if (skladem === 0) return polozky.map(() => null);
  return polozky.map((_, i) =>
    i === 0 ? nadpisSkladem(icz) : i === skladem ? NADPIS_OSTATNI_KODY : null,
  );
}

/** Časté položky zákazníka / provozovny (nejdřív provozovna, když nic nemá, celý zákazník). */
export async function nacistCasteProZakaznika(
  typy: readonly TypPolozky[],
  caste: NonNullable<VolbySeznamuPolozek['kontextZakaznika']>,
  signal: AbortSignal,
  sklad?: string,
  skladKontext?: KontextStavu,
): Promise<CasteVSeznamu<Polozka> | null> {
  const jedenTyp = typy.length === 1 ? typy[0] : undefined;
  const dotaz = async (provozovnaId?: string) => {
    const q = new URLSearchParams({
      nejcastejsi: 'zakaznik',
      pro_zakaznika_id: caste.zakaznikId,
      aktivni: 'true',
      velikost: String(jedenTyp ? POCET_CASTYCH : 50),
    });
    if (provozovnaId) q.set('pro_provozovnu_id', provozovnaId);
    if (jedenTyp) q.set('typ', jedenTyp);
    if (sklad) {
      q.set('sklad', sklad);
      if (skladKontext) q.set('sklad_kontext', skladKontext);
    }
    const r = await api.get<PolozkaSeznam>(`/polozky?${q.toString()}`, { signal });
    return r.polozky.filter((p) => typy.includes(p.typ)).slice(0, POCET_CASTYCH);
  };
  if (caste.provozovnaId) {
    const provozovna = await dotaz(caste.provozovnaId);
    if (provozovna.length > 0) return { nadpis: 'Časté u této provozovny', polozky: provozovna };
  }
  const zakaznik = await dotaz();
  return zakaznik.length > 0 ? { nadpis: 'Časté u tohoto zákazníka', polozky: zakaznik } : null;
}

/** Kolik položek smlouvy zákazníka se načte (zákazník jich má obvykle desítky). */
const POCET_VE_SMLOUVE = 500;

/**
 * Položky ze smluvních cen zákazníka (a provozovny), bez cen; při výpadku signálu kopie z tabletu (bez cen a bez stavu
 * skladu, i když se na něj pole ptá).
 */
export async function nacistPolozkyVeSmlouve(
  k: NonNullable<VolbySeznamuPolozek['kontextZakaznika']>,
  signal: AbortSignal,
  sklad?: string,
  skladKontext?: KontextStavu,
): Promise<Polozka[]> {
  const klic = `katalog-ve-smlouve:${k.zakaznikId}:${k.provozovnaId ?? ''}`;
  try {
    const q = new URLSearchParams({
      ve_smlouve_zakaznika_id: k.zakaznikId,
      aktivni: 'true',
      razeni: 'kod:asc',
      velikost: String(POCET_VE_SMLOUVE),
    });
    if (k.provozovnaId) q.set('ve_smlouve_provozovny_id', k.provozovnaId);
    if (sklad) {
      q.set('sklad', sklad);
      if (skladKontext) q.set('sklad_kontext', skladKontext);
    }
    const r = await api.get<PolozkaSeznam>(`/polozky?${q.toString()}`, { signal });
    await ulozitDoTabletu(klic, bezCen({ polozky: r.polozky, dalsiStrana: null }).polozky);
    return r.polozky;
  } catch (chyba) {
    if (jePoruchaSite(chyba)) {
      const ulozena = await ulozenoVTabletu<Polozka[]>(klic);
      if (ulozena) return ulozena.hodnota;
    }
    throw chyba;
  }
}

async function nacistVeSmlouve(
  typy: readonly TypPolozky[],
  k: NonNullable<VolbySeznamuPolozek['kontextZakaznika']>,
  signal: AbortSignal,
  sklad?: string,
  skladKontext?: KontextStavu,
): Promise<CasteVSeznamu<Polozka> | null> {
  const polozky = (await nacistPolozkyVeSmlouve(k, signal, sklad, skladKontext)).filter((p) =>
    typy.includes(p.typ),
  );
  return polozky.length > 0 ? { nadpis: 'Ve smlouvě zákazníka', polozky } : null;
}

/** Jak dlouho je předstažený seznam v tabletu čerstvý (h), než ho balíček na směnu stáhne znovu. */
const CERSTVOST_PREDSTAZENI_H = 6;
const TYPY_PREDSTAZENI: readonly TypPolozky[] = [
  'zpetny_odber',
  'material',
  'zbozi',
  'sluzba',
  'odpad',
];

/**
 * Předstažení pro Můj den bez signálu (volá balíček na směnu): seznamy typů, které řidič a skladník přidávají do
 * zakázky (první stránka, odpad prvních 100), a položky ze smlouvy zákazníků dnešních jízd. Chyby se potichu
 * přeskočí — je to příprava, ne práce řidiče. Seznamy se znovu nestahují dřív než za 6 hodin.
 */
export async function predstahniKatalogProTeren(
  jizdy: ReadonlyArray<{ zakaznik_id?: string | null; provozovna_id?: string | null }>,
): Promise<void> {
  if (jeOffline()) return;
  const signal = new AbortController().signal;
  const prace: Promise<unknown>[] = [];
  for (const typ of TYPY_PREDSTAZENI) {
    prace.push(
      (async () => {
        const kopie = await ulozenoVTabletu<unknown>(klicUlozeni([typ], {}, 1));
        if (kopie && Date.now() - kopie.cas < CERSTVOST_PREDSTAZENI_H * 3_600_000) return;
        await stranaSOfflinem(1, [typ], {}, signal);
      })().catch(() => undefined),
    );
  }
  const videno = new Set<string>();
  for (const j of jizdy) {
    if (!j.zakaznik_id) continue;
    const kontext = {
      zakaznikId: j.zakaznik_id,
      ...(j.provozovna_id ? { provozovnaId: j.provozovna_id } : {}),
    };
    const klic = `${kontext.zakaznikId}:${kontext.provozovnaId ?? ''}`;
    if (videno.has(klic)) continue;
    videno.add(klic);
    prace.push(nacistPolozkyVeSmlouve(kontext, signal).catch(() => undefined));
  }
  await Promise.all(prace);
}

/** Věta nad seznamem: u krátkého „klepněte a vyberte“, u dlouhého i s hledáním podle kódu (zadavatel 27. 10. 2026). */
export function napovedaSeznamuPolozek(
  typy: readonly TypPolozky[],
  celkem: number | undefined,
): string {
  const dlouhy = typy.includes('odpad') || (celkem ?? 0) > STRANKA_ODPADU;
  return dlouhy
    ? 'Vyberte ze seznamu, nebo pište kód (aspoň 2 číslice) či část názvu.'
    : 'Klepněte a vyberte ze seznamu, nebo pište kód či část názvu nebo popisu.';
}

/**
 * Seznam bez psaní pro pole „položka katalogu“. Čistá funkce bez hooků — volající ji může vyrobit při každém
 * vykreslení, klíče dotazů jsou stabilní.
 */
export function seznamBezTextuPolozek(volby: VolbySeznamuPolozek = {}): SeznamBezTextu<Polozka> {
  const typy = poradiTypu(volby.typy);
  const viceTypu = typy.length > 1;
  const klicTypu = typy.join(',');
  const kontext = volby.kontextZakaznika;
  const sklad = volby.sklad;
  const sklademNahore = Boolean(sklad && volby.sklademNahore);
  const oddilSkladem = (icz: string): OddilNahore<Polozka> => ({
    klicDotazu: [
      ...KLIC_KATALOG,
      'skladem-nahore',
      klicTypu,
      icz,
      volby.skladKontext ?? '',
      volby.zarizeniId ?? '',
      volby.zakaznikId ?? '',
      volby.proObcana ? 'obcan' : '',
      volby.cenikSd ? 'cenik-sd' : '',
    ],
    nacist: async (signal) => {
      const polozky = await nacistKodySkladem(typy, volby, signal);
      return polozky.length > 0 ? { nadpis: nadpisSkladem(icz), polozky } : null;
    },
  });
  const oddily = (
    k: NonNullable<VolbySeznamuPolozek['kontextZakaznika']>,
  ): OddilNahore<Polozka>[] => {
    const klic = [k.zakaznikId, k.provozovnaId ?? '', sklad ?? '', volby.skladKontext ?? ''];
    return [
      {
        klicDotazu: [...KLIC_KATALOG, 've-smlouve', klicTypu, ...klic],
        nacist: (signal) => nacistVeSmlouve(typy, k, signal, sklad, volby.skladKontext),
      },
      {
        klicDotazu: [...KLIC_KATALOG, 'caste', klicTypu, ...klic],
        nacist: (signal) => nacistCasteProZakaznika(typy, k, signal, sklad, volby.skladKontext),
      },
    ];
  };
  return {
    klicDotazu: [
      ...KLIC_KATALOG,
      'seznam-bez-textu',
      klicTypu,
      volby.zarizeniId ?? '',
      volby.zakaznikId ?? '',
      volby.proObcana ? 'obcan' : '',
      volby.cenikSd ? 'cenik-sd' : '',
      sklad ?? '',
      volby.skladKontext ?? '',
    ],
    nacist: (param, signal) => stranaSOfflinem(param, typy, volby, signal),
    skupina: (p) => nadpisSkupinyPolozky(p, viceTypu),
    ...(viceTypu
      ? { nadpisOstatnich: 'Všechny položky' }
      : sklademNahore
        ? { nadpisOstatnich: NADPIS_OSTATNI_KODY }
        : {}),
    napoveda: (celkem) => napovedaSeznamuPolozek(typy, celkem),
    ...(kontext || (sklademNahore && sklad)
      ? {
          oddily: [
            // Kódy skladem jsou nahoře nad vším ostatním; kód z oddílu se v dalších oddílech a v číselníku neopakuje.
            ...(sklademNahore && sklad ? [oddilSkladem(sklad)] : []),
            ...(kontext ? oddily(kontext) : []),
          ],
        }
      : {}),
    ...(sklad ? { platnostMin: PLATNOST_SE_STAVEM_SKLADU_MIN } : {}),
  };
}
